/**
 * <ym-cart-drawer>: the cart drawer (sections/ym-cart-drawer.liquid), and with data-page the cart
 * page (sections/ym-main-cart.liquid), which has the same contents (snippets/ym-cart-body.liquid)
 * but never opens or closes.
 *
 * - A modal <dialog> fixed to the right edge, on top of the page: opening it never moves or
 *   resizes anything behind it. It closes on X, a click on the backdrop and Esc.
 * - Opens from [data-ym-cart-open] (the header cart button) and after any add to cart that
 *   announces itself with a CartLinesUpdateEvent.
 * - Quantity, Remove and discount codes go through the Cart Ajax API; the drawer is then
 *   re-rendered with the Section Rendering API. A set row updates all of its lines at once.
 * - Save for later: the row's lines (variant, quantity, every property, so the personalization
 *   comes back as it was) leave the cart and wait in localStorage, drawn under the packages
 *   ([data-ym-saved]) until Move to cart or Remove. Needs browser storage: without it the button
 *   stays hidden (data-can-save). Saving shows a notice with Undo, which also says when the
 *   bundle discount drops (data-ship, data-any, data-tiers, data-mix, data-units on the rows) or the order
 *   falls under free shipping (Theme settings > Yallmark > Free shipping from).
 * - Empty cart: the season's bestsellers ([data-ym-empty-picks]) hide while Saved for later holds
 *   items.
 * - The free shipping bar (snippets/ym-cart-body.liquid) comes with each re-render; when its text
 *   changes ("$12.30 away…" → "You’ve unlocked free shipping") the status line says it too.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
// <ym-bundle-line>, the savings line: the drawer's re-rendered markup does not run its own scripts
import { linePct, parseTiers } from '@theme/ym-bundle';

const SAVED_KEY = 'ym-saved-for-later';
const SAVED_MAX = 20;

/** @typedef {{ id: number, quantity: number, properties: Record<string, string> }} SavedLine */
/**
 * @typedef {{ id: string, savedAt: number, url: string, title: string, image: string, detail: string,
 *   price: number, ship: string, any?: boolean, tiers: string, mix?: string, units: number,
 *   lines: SavedLine[] }} SavedItem
 */

/** @returns {SavedItem[]} */
function readSaved() {
  try {
    const list = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** @param {SavedItem[]} list @returns {boolean} false when the browser keeps nothing */
function writeSaved(list) {
  try {
    if (list.length) localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, SAVED_MAX)));
    else localStorage.removeItem(SAVED_KEY);
    return true;
  } catch {
    return false;
  }
}

function storageWorks() {
  try {
    localStorage.setItem('ym-storage-test', '1');
    localStorage.removeItem('ym-storage-test');
    return true;
  } catch {
    return false;
  }
}

class YmCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || 'ym-cart-drawer';
    this.page = this.hasAttribute('data-page');
    this.busy = false;
    /** The free shipping bar's new text after a re-render, said with the next announcement */
    this.shipNote = '';
    /** @type {{ text: string, undo?: string } | null} The notice after Save for later */
    this.notice = null;
    this.toggleAttribute('data-can-save', storageWorks());
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    if (!this.page) document.addEventListener('click', this.onOpenerClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    window.addEventListener('storage', this.onStorage);
    this.renderSaved();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOpenerClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    window.removeEventListener('storage', this.onStorage);
  }

  /** Saved in another tab (the cart page and a drawer elsewhere) */
  onStorage = (/** @type {StorageEvent} */ event) => {
    if (event.key === SAVED_KEY) this.renderSaved();
  };

  /** The drawer's <dialog>, or the cart page's container. */
  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ym-cart-dialog]'));
  }

  get root() {
    return /** @type {any} */ (window).Shopify?.routes?.root || '/';
  }

  /** @param {HTMLElement | null} [opener] */
  open(opener) {
    const dialog = this.dialog;
    if (this.page || !dialog || dialog.open) return;
    this.opener = opener || /** @type {HTMLElement | null} */ (document.activeElement);
    dialog.showModal();
    /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ym-cart-close]'))?.focus();
  }

  close() {
    const dialog = this.dialog;
    if (this.page || !dialog?.open) return;
    dialog.close();
    this.setNotice(null);
    this.opener?.focus?.({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  onOpenerClick = (event) => {
    const opener = /** @type {HTMLElement} */ (event.target).closest?.('[data-ym-cart-open]');
    if (!(opener instanceof HTMLElement)) return;
    event.preventDefault();
    this.open(opener);
    // The drawer was rendered with the page; refresh it in case the cart changed in another tab.
    this.refresh();
  };

  /** Re-render from an add to cart elsewhere on the page, and open on a successful add. */
  onCartLinesUpdate = (/** @type {any} */ event) => {
    if (event.detail?.source === 'ym-cart-drawer' || event.source === 'ym-cart-drawer') return;
    const isAdd = event.action === 'add';
    const promise = event.promise || Promise.resolve({});
    promise
      .then((/** @type {any} */ result) => {
        if (result?.detail?.source === 'ym-cart-drawer') return;
        return this.refresh().then(() => {
          if (isAdd && !result?.detail?.didError) this.open();
        });
      })
      .catch(() => {});
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const dialog = this.dialog;
    if (!this.page && (target === dialog || target.closest('[data-ym-cart-close]'))) {
      this.close();
      return;
    }

    const row = /** @type {HTMLElement | null} */ (target.closest('[data-ym-cart-row]'));
    const qty = target.closest('[data-ym-cart-qty]');
    if (row && qty instanceof HTMLElement) {
      if (qty.getAttribute('aria-disabled') === 'true') return;
      const next = Math.max(0, Number(row.dataset.qty || 1) + Number(qty.dataset.ymCartQty));
      this.setQuantity(row, next, `[data-ym-cart-qty="${qty.dataset.ymCartQty}"]`);
      return;
    }
    if (row && target.closest('[data-ym-cart-remove]')) {
      this.setQuantity(row, 0, null);
      return;
    }
    if (row && target.closest('[data-ym-cart-save]')) {
      this.saveRow(row);
      return;
    }

    const move = target.closest('[data-ym-saved-move]');
    if (move instanceof HTMLElement) {
      this.moveSaved(move.dataset.ymSavedMove || '');
      return;
    }
    const drop = target.closest('[data-ym-saved-remove]');
    if (drop instanceof HTMLElement) {
      writeSaved(readSaved().filter((item) => item.id !== drop.dataset.ymSavedRemove));
      this.renderSaved();
      this.announce('Removed from Saved for later.');
      /** @type {HTMLElement | null} */ (this.querySelector(this.page ? '#YmCartTitle' : '[data-ym-cart-close]'))?.focus();
      return;
    }
    const undo = target.closest('[data-ym-notice-undo]');
    if (undo instanceof HTMLElement) {
      this.moveSaved(undo.dataset.ymNoticeUndo || '');
      return;
    }

    const removeCode = target.closest('[data-ym-cart-code-remove]');
    if (removeCode instanceof HTMLElement) {
      const code = removeCode.dataset.ymCartCodeRemove || '';
      this.setCodes(this.codes().filter((c) => c !== code), 'Discount code removed.');
    }
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-ym-cart-discount]')) return;
    event.preventDefault();
    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="code"]'));
    const code = (input?.value || '').trim();
    if (!code) return;
    const codes = this.codes();
    if (!codes.includes(code)) codes.push(code);
    this.setCodes(codes, `Discount code ${code} applied.`, code);
  };

  /** Codes currently on the cart, from the rendered chips. */
  codes() {
    return Array.from(this.querySelectorAll('[data-ym-cart-code-remove]')).map(
      (el) => /** @type {HTMLElement} */ (el).dataset.ymCartCodeRemove || ''
    );
  }

  /**
   * Set the quantity of every line in a row (a set has two).
   * @param {HTMLElement} row @param {number} quantity @param {string | null} focusSelector
   */
  async setQuantity(row, quantity, focusSelector) {
    const keys = (row.dataset.keys || '').split(',').filter(Boolean);
    if (!keys.length || this.busy) return;
    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = quantity;
    row.setAttribute('aria-busy', 'true');
    const rowIndex = Array.from(this.querySelectorAll('[data-ym-cart-row]')).indexOf(row);
    await this.update(
      { updates },
      quantity === 0 ? 'remove' : 'update',
      quantity === 0 ? 'Item removed from your cart.' : `Quantity updated to ${quantity}.`
    );
    // Keep focus where the shopper was: the same button on the same row, or the close button.
    const rows = this.querySelectorAll('[data-ym-cart-row]');
    const sameRow = rows[Math.min(rowIndex, rows.length - 1)];
    const focusEl =
      (focusSelector && sameRow?.querySelector(focusSelector)) ||
      this.querySelector(this.page ? '#YmCartTitle' : '[data-ym-cart-close]');
    /** @type {HTMLElement | null} */ (focusEl)?.focus();
  }

  /** @param {string[]} codes @param {string} message @param {string} [added] */
  async setCodes(codes, message, added) {
    const cart = await this.update({ discount: codes.join(',') }, 'update', message);
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('#YmCartCode'));
    // Shopify keeps an unusable code on the cart without applying it; say so instead of failing silently.
    if (added && cart) {
      const found = (cart.discount_codes || []).find(
        (/** @type {any} */ d) => String(d.code).toLowerCase() === added.toLowerCase()
      );
      if (!found?.applicable) {
        const text = found ? `${added} can’t be used with this cart.` : `${added} isn’t a valid code.`;
        const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-cart-code-error]'));
        if (error) {
          error.textContent = text;
          error.hidden = false;
        }
        if (input) input.value = added;
        this.announce(text);
      }
    }
    input?.focus();
  }

  /* ---------- save for later ---------- */

  /** Items of a bundle group in the cart, from the rendered rows. @param {string} ship */
  shipUnits(ship) {
    return this.rowUnits((row) => Boolean(ship) && row.dataset.ship === ship);
  }

  /** Items of the mix-and-match collection (tag bundle-any) in the cart. */
  anyUnits() {
    return this.rowUnits((row) => row.dataset.any === '1');
  }

  /** @param {(row: HTMLElement) => boolean} test */
  rowUnits(test) {
    return Array.from(/** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-ym-cart-row]')))
      .filter(test)
      .reduce((n, row) => n + (Number(row.dataset.units) || 0), 0);
  }

  /**
   * The bundle discount of each group in the cart (the better offer), before and after taking
   * `units` items of the group `ship` (in the mix-and-match collection or not) out.
   * @param {string} ship @param {boolean} any @param {number} units
   * @returns {{ ship: string, before: number, after: number, left: number }[]}
   */
  bundleDrops(ship, any, units) {
    const anyBefore = this.anyUnits();
    const anyAfter = anyBefore - (any ? units : 0);
    /** @type {Map<string, HTMLElement>} */
    const groups = new Map();
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-ym-cart-row]'))) {
      if (row.dataset.ship && !groups.has(row.dataset.ship)) groups.set(row.dataset.ship, row);
    }
    return [...groups].map(([key, row]) => {
      const offer = { same: parseTiers(row.dataset.tiers), mix: parseTiers(row.dataset.mix) };
      const sameBefore = this.shipUnits(key);
      const left = sameBefore - (key === ship ? units : 0);
      const isAny = row.dataset.any === '1';
      return {
        ship: key,
        before: linePct(offer, sameBefore, isAny ? anyBefore : 0),
        after: linePct(offer, left, isAny ? anyAfter : 0),
        left,
      };
    });
  }

  /** @param {number} cents */
  money(cents) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-saved]'));
    return formatMoney(cents, box?.dataset.moneyFormat || '${{amount}}', box?.dataset.currency || 'USD');
  }

  /**
   * Takes a row out of the cart into Saved for later, then says what changed, with Undo.
   * @param {HTMLElement} row
   */
  async saveRow(row) {
    const keys = (row.dataset.keys || '').split(',').filter(Boolean);
    if (!keys.length || this.busy) return;
    row.setAttribute('aria-busy', 'true');
    let cart;
    try {
      cart = await (await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      row.removeAttribute('aria-busy');
      this.announce('Could not save this item. Please try again.');
      return;
    }
    const lines = keys.map((key) => (cart.items || []).find((/** @type {any} */ line) => line.key === key)).filter(Boolean);
    if (!lines.length) {
      await this.refresh();
      return;
    }

    const image = /** @type {HTMLImageElement | null} */ (row.querySelector('.ym-cart__media img'));
    const detail = [
      row.querySelector('.ym-cart__variant')?.textContent?.trim(),
      ...Array.from(row.querySelectorAll('.ym-cart__print-line')).map((el) => el.textContent?.trim()),
    ].filter(Boolean);
    /** @type {SavedItem} */
    const item = {
      id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      savedAt: Date.now(),
      url: row.dataset.url || '',
      title: row.querySelector('.ym-cart__name')?.textContent?.trim() || '',
      image: image?.currentSrc || image?.src || '',
      detail: detail.join(' · '),
      price: lines.reduce((/** @type {number} */ n, /** @type {any} */ line) => n + line.original_line_price, 0),
      ship: row.dataset.ship || '',
      any: row.dataset.any === '1',
      tiers: row.dataset.tiers || '',
      mix: row.dataset.mix || '',
      units: Number(row.dataset.units) || 0,
      lines: lines.map((/** @type {any} */ line) => ({ id: line.variant_id, quantity: line.quantity, properties: line.properties || {} })),
    };
    if (!writeSaved([item, ...readSaved()])) {
      row.removeAttribute('aria-busy');
      this.announce('Could not save this item. Please try again.');
      return;
    }

    // What the cart gives up: a bundle discount on the items left, free shipping
    const drop = this.bundleDrops(item.ship, Boolean(item.any), item.units).find((g) => g.left > 0 && g.after < g.before);
    const freeShip = Number(/** @type {HTMLElement | null} */ (this.querySelector('[data-ym-saved]'))?.dataset.freeShip) || 0;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;
    const after = await this.update({ updates }, 'remove', 'Saved for later.');
    if (!after) {
      writeSaved(readSaved().filter((saved) => saved.id !== item.id));
      this.renderSaved();
      return;
    }

    const parts = ['Saved for later.'];
    if (drop) {
      parts.push(
        drop.after
          ? `Your bundle now saves ${drop.after}% instead of ${drop.before}%.`
          : `The bundle discount no longer applies to the ${drop.left === 1 ? 'item' : 'items'} left.`
      );
    }
    if (freeShip && cart.total_price >= freeShip && after.item_count > 0 && after.total_price < freeShip) {
      // "$75", not "$75.00"
      parts.push(`Your order is now under ${this.money(freeShip).replace(/[.,]00(?!\d)/, '')}, so shipping isn’t free.`);
    }
    this.setNotice({ text: parts.join(' '), undo: item.id });
    /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-notice-undo]'))?.focus();
  }

  /** Puts a saved item back in the cart, personalization and all. @param {string} id */
  async moveSaved(id) {
    const item = readSaved().find((saved) => saved.id === id);
    if (!item || this.busy) return;
    const button = this.querySelector(`[data-ym-saved-move="${id}"], [data-ym-notice-undo="${id}"]`);
    button?.setAttribute('aria-busy', 'true');
    const cart = await this.update({ items: item.lines }, 'add', 'Moved to your cart.');
    if (!cart) {
      this.setNotice({ text: `${item.title || 'This item'} can’t go back in the cart right now. It may be sold out.` });
      return;
    }
    writeSaved(readSaved().filter((saved) => saved.id !== id));
    this.renderSaved();
    /** @type {HTMLElement | null} */ (this.querySelector(this.page ? '#YmCartTitle' : '[data-ym-cart-close]'))?.focus();
  }

  /** @param {{ text: string, undo?: string } | null} notice */
  setNotice(notice) {
    this.notice = notice;
    this.placeNotice();
  }

  /** The notice sits above the total in the drawer, on top of the items on the cart page. */
  placeNotice() {
    this.querySelector('[data-ym-cart-notice]')?.remove();
    const notice = this.notice;
    if (!notice) return;
    const el = document.createElement('div');
    el.className = 'ym-cart__notice';
    el.dataset.ymCartNotice = '';
    el.setAttribute('role', 'status');
    const text = document.createElement('span');
    text.textContent = notice.text;
    el.append(text);
    if (notice.undo) {
      const undo = document.createElement('button');
      undo.type = 'button';
      undo.className = 'ym-cart__notice-undo';
      undo.dataset.ymNoticeUndo = notice.undo;
      undo.textContent = 'Undo';
      el.append(undo);
    }
    const foot = this.querySelector('.ym-cart__foot');
    if (!this.page && foot) foot.before(el);
    else this.querySelector('.ym-cart__items')?.prepend(el);
  }

  /** Draws Saved for later under the packages (or under an empty cart). */
  renderSaved() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-saved]'));
    if (!box) return;
    const list = this.hasAttribute('data-can-save') ? readSaved() : [];
    box.hidden = !list.length;
    // Empty cart: the shopper's own saved items come before the season's bestsellers
    this.querySelector('[data-ym-empty-picks]')?.toggleAttribute('hidden', list.length > 0);
    if (!list.length) {
      box.replaceChildren();
      return;
    }
    const total = Number(box.dataset.total) || 0;
    const freeShip = Number(box.dataset.freeShip) || 0;

    const head = document.createElement('h3');
    head.className = 'ym-saved__title';
    head.textContent = `Saved for later (${list.length})`;
    const ul = document.createElement('ul');
    ul.className = 'ym-saved__list';
    ul.setAttribute('role', 'list');
    for (const item of list) {
      // Why move it back: a bundle discount with the cart's items, or free shipping
      let hint = '';
      const units = this.shipUnits(item.ship);
      const any = item.any ? this.anyUnits() : 0;
      if (units > 0 || any > 0) {
        const offer = { same: parseTiers(item.tiers), mix: parseTiers(item.mix) };
        const pct = linePct(offer, units + item.units, item.any ? any + item.units : 0);
        if (pct > linePct(offer, units, any)) {
          hint = units > 0 ? `Ships with your cart items: move it back to save ${pct}%.` : `Move it back to save ${pct}% with your cart items.`;
        }
      }
      if (!hint && freeShip && total > 0 && total < freeShip && total + item.price >= freeShip) {
        hint = 'Move it back to get free shipping.';
      }

      const li = document.createElement('li');
      li.className = 'ym-saved__item';
      li.innerHTML = `
        <a class="ym-saved__media" tabindex="-1" aria-hidden="true"></a>
        <div class="ym-saved__info">
          <div class="ym-saved__top"><a class="ym-saved__name"></a><span class="ym-saved__price"></span></div>
          <span class="ym-saved__detail"></span>
          <span class="ym-saved__hint"></span>
          <div class="ym-saved__actions">
            <button type="button" class="ym-saved__move">Move to cart</button>
            <button type="button" class="ym-cart__remove">Remove</button>
          </div>
        </div>`;
      const media = /** @type {HTMLAnchorElement} */ (li.querySelector('.ym-saved__media'));
      const name = /** @type {HTMLAnchorElement} */ (li.querySelector('.ym-saved__name'));
      if (item.url) {
        media.href = item.url;
        name.href = item.url;
      }
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        media.append(img);
      }
      name.textContent = item.title;
      /** @type {HTMLElement} */ (li.querySelector('.ym-saved__price')).textContent = this.money(item.price);
      const detail = /** @type {HTMLElement} */ (li.querySelector('.ym-saved__detail'));
      if (item.detail) detail.textContent = item.detail;
      else detail.remove();
      const hintEl = /** @type {HTMLElement} */ (li.querySelector('.ym-saved__hint'));
      if (hint) hintEl.textContent = hint;
      else hintEl.remove();
      const move = /** @type {HTMLElement} */ (li.querySelector('.ym-saved__move'));
      move.dataset.ymSavedMove = item.id;
      move.setAttribute('aria-label', `Move ${item.title} to cart`);
      const remove = /** @type {HTMLElement} */ (li.querySelector('.ym-cart__remove'));
      remove.dataset.ymSavedRemove = item.id;
      remove.setAttribute('aria-label', `Remove ${item.title} from Saved for later`);
      ul.append(li);
    }
    box.replaceChildren(head, ul);
  }

  /**
   * POST to /cart/update.js (or /cart/add.js with items), tell the rest of the theme (cart bubble)
   * and re-render.
   * @param {Record<string, unknown>} body @param {'update' | 'remove' | 'add'} action @param {string} message
   */
  async update(body, action, message) {
    this.busy = true;
    this.notice = null;
    let resolve = (/** @type {any} */ _) => {};
    let reject = (/** @type {any} */ _) => {};
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    promise.catch(() => {});
    /** @type {Record<string, number>} */
    const updates = /** @type {any} */ (body).updates || {};
    /** @type {SavedLine[] | undefined} */
    const items = /** @type {any} */ (body).items;
    const lines = items
      ? items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }))
      : Object.entries(updates).map(([key, quantity]) => ({ lineKey: key, quantity }));
    // Line changes are announced so the cart bubble follows; a discount code changes no lines.
    if (lines.length) this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise }));

    try {
      const response = await fetch(`${this.root}cart/${items ? 'add' : 'update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(items ? body : { ...body, sections: this.sectionId }),
      });
      let cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');
      // /cart/add.js answers with the added lines only
      if (items) cart = await (await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'ym-cart-drawer', didError: false },
      });
      const html = cart.sections?.[this.sectionId];
      if (html) this.render(html);
      else await this.refresh();
      this.announce(message);
      return cart;
    } catch (error) {
      reject(error);
      await this.refresh();
      this.announce('Could not update your cart. Please try again.');
      return null;
    } finally {
      this.busy = false;
    }
  }

  /** Fetch the section and swap the dialog's content. */
  async refresh() {
    this.notice = null;
    try {
      // A template section renders only on its own page.
      const path = this.page ? `${this.root}cart` : this.root;
      const response = await fetch(`${path}?sections=${encodeURIComponent(this.sectionId)}`, { cache: 'no-store' });
      const data = await response.json();
      if (data[this.sectionId]) this.render(data[this.sectionId]);
      // Not a change made here (an add elsewhere, another tab): nothing to say with it
      this.shipNote = '';
    } catch {
      // Keep what is on screen; the next change or page load brings it up to date.
    }
  }

  /** @param {string} html */
  render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-ym-cart-dialog]');
    const dialog = this.dialog;
    if (!next || !dialog) return;
    const discountOpen = dialog.querySelector('.ym-cart__discount')?.hasAttribute('open');
    // The free shipping bar is new markup each time: its change goes with the next announcement
    const shipBefore = dialog.querySelector('[data-ym-ship-text]')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    dialog.replaceChildren(...Array.from(next.childNodes).map((n) => document.importNode(n, true)));
    const shipAfter = dialog.querySelector('[data-ym-ship-text]')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    this.shipNote = shipAfter && shipAfter !== shipBefore ? `${shipAfter}.` : '';
    dialog.className = next.className;
    if (discountOpen) dialog.querySelector('.ym-cart__discount')?.setAttribute('open', '');
    this.renderSaved();
    this.placeNotice();
  }

  /** @param {string} message Said with the free shipping bar's change, if any. */
  announce(message) {
    const status = this.querySelector('[data-ym-cart-status]');
    if (status) status.textContent = [message, this.shipNote].filter(Boolean).join(' ');
    this.shipNote = '';
  }
}

if (!customElements.get('ym-cart-drawer')) customElements.define('ym-cart-drawer', YmCartDrawer);
